import { PAGE_SIZE_OPTIONS } from "../../hooks/useGridData.ts";
import styles from "./pagination.module.scss";

interface PaginationProps {
  page: number;
  pageSize: number;
  rowCount: number;
  knownTotal: number | null;
  countLoading: boolean;
  /** Why the last count produced no number; shown instead of the count button. */
  countError?: string | null;
  uniqueCount: number | null | undefined;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onRequestCount: () => void;
}

export function Pagination({
  page,
  pageSize,
  rowCount,
  knownTotal,
  countLoading,
  countError,
  uniqueCount,
  onPageChange,
  onPageSizeChange,
  onRequestCount,
}: PaginationProps) {
  const start = page * pageSize + 1;
  const end = knownTotal !== null
    ? Math.min(start + pageSize - 1, knownTotal)
    : start + rowCount - 1;
  const totalPages = knownTotal !== null ? Math.max(1, Math.ceil(knownTotal / pageSize)) : null;
  const showUniqueCount = Number.isFinite(uniqueCount) && uniqueCount !== knownTotal;

  const canGoPrev = page > 0;
  const canGoNext = totalPages !== null ? page < totalPages - 1 : rowCount >= pageSize;

  return (
    <div className={styles.pager} role="navigation" aria-label="Table pagination">
      {/* Page navigation — keeps the arrows + page label together so they
          wrap/center as one unit on narrow screens. */}
      <div className={styles.navGroup}>
        <button onClick={() => onPageChange(0)} disabled={!canGoPrev} aria-label="First page">«</button>
        <button onClick={() => onPageChange(page - 1)} disabled={!canGoPrev} aria-label="Previous page">‹</button>
        <span aria-live="polite">
          Page {page + 1}{totalPages !== null ? ` of ${totalPages}` : ""}
        </span>
        <button onClick={() => onPageChange(page + 1)} disabled={!canGoNext} aria-label="Next page">›</button>
        <button
          onClick={() => totalPages !== null && onPageChange(totalPages - 1)}
          disabled={totalPages === null || !canGoNext}
          aria-label="Last page"
        >»</button>
      </div>
      {/* Row count + page size — the second logical unit. */}
      <div className={styles.metaGroup}>
        <span>
          {start}–{end}{" "}
          {countLoading
            ? "counting..."
            : knownTotal !== null
              ? <>of {knownTotal}{showUniqueCount ? ` (${uniqueCount} unique)` : ""}</>
              : countError
                ? <span className={styles.countNote} role="status">{countError}</span>
                : <button onClick={onRequestCount} className={styles.countBtn} title="Count all matching rows">count</button>
          }
        </span>
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          aria-label="Rows per page"
        >
          {PAGE_SIZE_OPTIONS.map((size) => (
            <option key={size} value={size}>
              {size} rows
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
